const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const impersonateId = sessionStorage.getItem('impersonatingId');
  if (impersonateId) headers['X-Impersonate-As'] = impersonateId;
  return headers;
};

const handleResponse = async (response) => {
  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error(response.ok ? 'Unexpected server response' : `Server error (${response.status})`);
  }
  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }
  return data;
};

// Auth Service
export const authService = {
  login: async (loginId, password) => {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ loginId, password })
    });
    return handleResponse(response);
  },

  register: async (firstName, lastName, loginId, password) => {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName, lastName, loginId, password })
    });
    return handleResponse(response);
  },

  getCurrentUser: async () => {
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: getAuthHeader()
    });
    const data = await handleResponse(response);
    return data.user;
  }
};

// Invoice API Service
export const apiService = {
  getInvoices: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        queryParams.append(key, value);
      }
    });

    const response = await fetch(`${API_URL}/invoices?${queryParams}`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getInvoice: async (id) => {
    const response = await fetch(`${API_URL}/invoices/${id}`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  uploadInvoice: async (formData) => {
    const response = await fetch(`${API_URL}/invoices`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    const data = await handleResponse(response);
    return data.invoice;
  },

  updateInvoice: async (id, data) => {
    const isFormData = data instanceof FormData;
    const response = await fetch(`${API_URL}/invoices/${id}`, {
      method: 'PUT',
      headers: isFormData
        ? getAuthHeader()
        : { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: isFormData ? data : JSON.stringify(data)
    });
    const result = await handleResponse(response);
    return result.invoice;
  },

  updateStatus: async (id, status) => {
    const response = await fetch(`${API_URL}/invoices/${id}/status`, {
      method: 'PATCH',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status })
    });
    const data = await handleResponse(response);
    return data.invoice;
  },

  addAttachment: async (invoiceId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/invoices/${invoiceId}/attachments`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    const result = await handleResponse(response);
    return result.attachment;
  },

  deleteAttachment: async (invoiceId, attachmentId) => {
    const response = await fetch(`${API_URL}/invoices/${invoiceId}/attachments/${attachmentId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  deleteInvoice: async (id) => {
    const response = await fetch(`${API_URL}/invoices/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  downloadInvoice: async (id, filename) => {
    const response = await fetch(`${API_URL}/invoices/${id}/download`, {
      headers: getAuthHeader()
    });

    if (!response.ok) {
      throw new Error('Download failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }
};

// Analytics API Service
export const analyticsService = {
  getDailyExpenses: async (date) => {
    const response = await fetch(`${API_URL}/analytics/daily?date=${date}`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getRangeExpenses: async (startDate, endDate) => {
    const response = await fetch(`${API_URL}/analytics/range?startDate=${startDate}&endDate=${endDate}`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getMonthlyExpenses: async (month) => {
    const response = await fetch(`${API_URL}/analytics/monthly?month=${month}`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getSummary: async () => {
    const response = await fetch(`${API_URL}/analytics/summary`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getByCategory: async () => {
    const response = await fetch(`${API_URL}/analytics/by-category`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getByVendor: async () => {
    const response = await fetch(`${API_URL}/analytics/by-vendor`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  exportCSV: async (startDate, endDate) => {
    const url = `${API_URL}/analytics/export?startDate=${startDate}&endDate=${endDate}`;
    window.open(url, '_blank');
  }
};

// Recurring Expenses API Service
export const recurringExpenseService = {
  getAll: async () => {
    const response = await fetch(`${API_URL}/recurring-expenses`, {
      headers: getAuthHeader()
    });
    const data = await handleResponse(response);
    return data.expenses;
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/recurring-expenses`, {
      method: 'POST',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    const result = await handleResponse(response);
    return result.expense;
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/recurring-expenses/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    const result = await handleResponse(response);
    return result.expense;
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/recurring-expenses/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  }
};

// Settings API Service
export const settingsService = {
  get: async (key) => {
    const response = await fetch(`${API_URL}/settings/${key}`, {
      headers: getAuthHeader()
    });
    const data = await handleResponse(response);
    return data.value;
  },

  set: async (key, value) => {
    const response = await fetch(`${API_URL}/settings/${key}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ value })
    });
    return handleResponse(response);
  },

  getDbStats: async () => {
    const response = await fetch(`${API_URL}/settings/db/stats`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getUploadsStats: async () => {
    const response = await fetch(`${API_URL}/settings/uploads/stats`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getPermissions: async () => {
    const response = await fetch(`${API_URL}/settings/permissions`);
    const data = await handleResponse(response);
    return data.permissions;
  },

  setPermissions: async (permissions) => {
    const response = await fetch(`${API_URL}/settings/permissions`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ permissions })
    });
    return handleResponse(response);
  },

  getUserPermissions: async (userId) => {
    const response = await fetch(`${API_URL}/settings/permissions/user/${userId}`, {
      headers: getAuthHeader()
    });
    const data = await handleResponse(response);
    return data.overrides || {};
  },

  setUserPermissions: async (userId, overrides) => {
    const response = await fetch(`${API_URL}/settings/permissions/user/${userId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ overrides })
    });
    return handleResponse(response);
  },

  getBranding: async () => {
    const response = await fetch(`${API_URL}/settings/branding`);
    return handleResponse(response);
  },

  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await fetch(`${API_URL}/settings/branding/logo`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    return handleResponse(response);
  },

  resetLogo: async () => {
    const response = await fetch(`${API_URL}/settings/brand_logo_path`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: null })
    });
    return handleResponse(response);
  },

  downloadBackup: async () => {
    const response = await fetch(`${API_URL}/settings/db/backup`, {
      headers: getAuthHeader()
    });
    if (!response.ok) throw new Error('Backup failed');
    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1].replace(/"/g, '')
      : `berlinr-backup-${new Date().toISOString().slice(0, 10)}.zip`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  restoreBackup: async (file, { restoreFiles }) => {
    const formData = new FormData();
    formData.append('backup', file);
    formData.append('confirm', 'RESTORE');
    formData.append('restoreFiles', String(!!restoreFiles));
    const response = await fetch(`${API_URL}/settings/db/restore`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    return handleResponse(response);
  }
};

// Timesheet API Service
export const timesheetService = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.append(k, v); });
    const response = await fetch(`${API_URL}/timesheets?${query}`, {
      headers: getAuthHeader()
    });
    const data = await handleResponse(response);
    return data.timesheets;
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/timesheets`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await handleResponse(response);
    return result.timesheet;
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/timesheets/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await handleResponse(response);
    return result.timesheet;
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/timesheets/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getAccess: async () => {
    const response = await fetch(`${API_URL}/timesheets/access`, {
      headers: getAuthHeader()
    });
    const data = await handleResponse(response);
    return data.access;
  },

  setAccess: async (adminId, employeeIds) => {
    const response = await fetch(`${API_URL}/timesheets/access/${adminId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ employeeIds })
    });
    return handleResponse(response);
  }
};

// Users API Service
export const usersService = {
  createUser: async (data) => {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    const result = await handleResponse(response);
    return result.user;
  },

  getUsers: async () => {
    const response = await fetch(`${API_URL}/users`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getPreferences: async () => {
    const response = await fetch(`${API_URL}/users/me/preferences`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  savePreferences: async (prefs) => {
    const response = await fetch(`${API_URL}/users/me/preferences`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(prefs)
    });
    return handleResponse(response);
  },

  getArchivedUsers: async () => {
    const response = await fetch(`${API_URL}/users?archived=true`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  updateProfile: async (id, data) => {
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    const result = await handleResponse(response);
    return result.user;
  },

  updatePassword: async (id, passwordData) => {
    const response = await fetch(`${API_URL}/users/${id}/password`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(passwordData)
    });
    return handleResponse(response);
  },

  updateUserRole: async (id, role) => {
    const response = await fetch(`${API_URL}/users/${id}/role`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ role })
    });
    return handleResponse(response);
  },

  resetPassword: async (id, password) => {
    const response = await fetch(`${API_URL}/users/${id}/reset-password`, {
      method: 'PUT',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(password ? { password } : {})
    });
    return handleResponse(response);
  },

  uploadPhoto: async (id, file) => {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await fetch(`${API_URL}/users/${id}/photo`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    const result = await handleResponse(response);
    return result.user;
  },

  removePhoto: async (id) => {
    const response = await fetch(`${API_URL}/users/${id}/photo`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    const result = await handleResponse(response);
    return result.user;
  },

  deleteUser: async (id) => {
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  archiveUser: async (id) => {
    const response = await fetch(`${API_URL}/users/${id}/archive`, {
      method: 'PUT',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  unarchiveUser: async (id) => {
    const response = await fetch(`${API_URL}/users/${id}/unarchive`, {
      method: 'PUT',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  }
};

// Employee Documents API Service
export const employeeDocService = {
  getDocuments: async (userId) => {
    const response = await fetch(`${API_URL}/users/${userId}/documents`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  uploadDocument: async (userId, type, files) => {
    const formData = new FormData();
    formData.append('type', type);
    files.forEach(f => formData.append('files', f));
    const response = await fetch(`${API_URL}/users/${userId}/documents`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    return handleResponse(response);
  },

  deleteDocument: async (userId, docId) => {
    const response = await fetch(`${API_URL}/users/${userId}/documents/${docId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  }
};

// Credit API Service
export const creditService = {
  getTransactions: async (userId) => {
    const response = await fetch(`${API_URL}/credits/${userId}`, {
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  addCredit: async (userId, data) => {
    const response = await fetch(`${API_URL}/credits/${userId}`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  }
};

// Vacation API Service
export const vacationService = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') query.append(k, v); });
    const response = await fetch(`${API_URL}/vacations?${query}`, { headers: getAuthHeader() });
    const data = await handleResponse(response);
    return data.vacations;
  },

  getBalance: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') query.append(k, v); });
    const response = await fetch(`${API_URL}/vacations/balance?${query}`, { headers: getAuthHeader() });
    const data = await handleResponse(response);
    return data.balance;
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/vacations`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await handleResponse(response);
    return result.vacation;
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/vacations/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await handleResponse(response);
    return result.vacation;
  },

  approve: async (id, comment = '') => {
    const response = await fetch(`${API_URL}/vacations/${id}/approve`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment })
    });
    const result = await handleResponse(response);
    return result.vacation;
  },

  reject: async (id, comment = '') => {
    const response = await fetch(`${API_URL}/vacations/${id}/reject`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment })
    });
    const result = await handleResponse(response);
    return result.vacation;
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/vacations/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  }
};

// Todo API Service
export const todoService = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.append(k, v); });
    const response = await fetch(`${API_URL}/todos?${query}`, { headers: getAuthHeader() });
    const data = await handleResponse(response);
    return data.todos;
  },

  getUsers: async () => {
    const response = await fetch(`${API_URL}/todos/users`, { headers: getAuthHeader() });
    const data = await handleResponse(response);
    return data.users;
  },

  create: async (data) => {
    const { screenshot, ...rest } = data;
    let response;
    if (screenshot) {
      const formData = new FormData();
      Object.entries(rest).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        formData.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
      });
      formData.append('screenshot', screenshot);
      response = await fetch(`${API_URL}/todos`, { method: 'POST', headers: getAuthHeader(), body: formData });
    } else {
      response = await fetch(`${API_URL}/todos`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(rest)
      });
    }
    const result = await handleResponse(response);
    return result.todo;
  },

  update: async (id, data) => {
    const { screenshot, ...rest } = data;
    let response;
    if (screenshot) {
      const formData = new FormData();
      Object.entries(rest).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        formData.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
      });
      formData.append('screenshot', screenshot);
      response = await fetch(`${API_URL}/todos/${id}`, { method: 'PUT', headers: getAuthHeader(), body: formData });
    } else {
      response = await fetch(`${API_URL}/todos/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(rest)
      });
    }
    const result = await handleResponse(response);
    return result.todo;
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/todos/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  reorder: async (orderedIds) => {
    const response = await fetch(`${API_URL}/todos/reorder`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderedIds })
    });
    return handleResponse(response);
  },

  review: async (id, data) => {
    const response = await fetch(`${API_URL}/todos/${id}/review`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await handleResponse(response);
    return result.todo;
  }
};

export const reminderService = {
  getAll: async (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.append(k, v); });
    const response = await fetch(`${API_URL}/reminders?${query}`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  getUsers: async () => {
    const response = await fetch(`${API_URL}/reminders/users`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_URL}/reminders`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_URL}/reminders/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_URL}/reminders/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  }
};

export const trainingMaterialService = {
  getInfo: async () => {
    const response = await fetch(`${API_URL}/trainings/material`, { headers: getAuthHeader() });
    const data = await handleResponse(response);
    return data.material; // null or { name, uploadedAt }
  },

  upload: async (file) => {
    const formData = new FormData();
    formData.append('material', file);
    const response = await fetch(`${API_URL}/trainings/material`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    const data = await handleResponse(response);
    return data.material;
  },

  download: async (filename) => {
    const response = await fetch(`${API_URL}/trainings/material/download`, { headers: getAuthHeader() });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  remove: async () => {
    const response = await fetch(`${API_URL}/trainings/material`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  }
};

export const quizService = {
  saveAttempt: async ({ quizId, level, score, total, timeTaken, answers }) => {
    const response = await fetch(`${API_URL}/quiz/attempts`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ quizId, level, score, total, timeTaken, answers })
    });
    return handleResponse(response);
  },

  getMyAttempts: async () => {
    const response = await fetch(`${API_URL}/quiz/attempts/mine`, {
      headers: getAuthHeader()
    });
    const data = await handleResponse(response);
    return data.attempts;
  },

  getAllAttempts: async () => {
    const response = await fetch(`${API_URL}/quiz/attempts/all`, {
      headers: getAuthHeader()
    });
    const data = await handleResponse(response);
    return data.attempts;
  },

  deleteAttempt: async (id) => {
    const response = await fetch(`${API_URL}/quiz/attempts/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  getSettings: async () => {
    const response = await fetch(`${API_URL}/quiz/settings`, { headers: getAuthHeader() });
    const data = await handleResponse(response);
    return data.limits; // { 1: number|null, 2: number|null }
  },

  updateSettings: async (limits) => {
    const response = await fetch(`${API_URL}/quiz/settings`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ limits })
    });
    return handleResponse(response);
  }
};

export const salesInvoiceService = {
  list: async () => {
    const response = await fetch(`${API_URL}/sales-invoices`, { headers: getAuthHeader() });
    return handleResponse(response);
  },

  getNextNumber: async (invoiceDate) => {
    const qs = invoiceDate ? `?invoiceDate=${encodeURIComponent(invoiceDate)}` : '';
    const response = await fetch(`${API_URL}/sales-invoices/next-number${qs}`, { headers: getAuthHeader() });
    return handleResponse(response);
  },

  skipExistingNumber: async (id) => {
    const response = await fetch(`${API_URL}/sales-invoices/${id}/skip-number`, {
      method: 'POST',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },

  skipNumber: async (invoiceNumber, invoiceId) => {
    const response = await fetch(`${API_URL}/sales-invoices/skip-number`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoiceNumber, invoiceId })
    });
    return handleResponse(response);
  },

  get: async (id) => {
    const response = await fetch(`${API_URL}/sales-invoices/${id}`, { headers: getAuthHeader() });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/sales-invoices`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/sales-invoices/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  updateStatus: async (id, status, formData) => {
    const response = await fetch(`${API_URL}/sales-invoices/${id}/status`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, ...(formData ? { formData } : {}) })
    });
    return handleResponse(response);
  },

  history: async (id) => {
    const response = await fetch(`${API_URL}/sales-invoices/${id}/history`, { headers: getAuthHeader() });
    return handleResponse(response);
  },

  delete: async (id) => {
    const response = await fetch(`${API_URL}/sales-invoices/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  }
};

export const salesQuotationService = {
  list: async () => {
    const response = await fetch(`${API_URL}/sales-quotations`, { headers: getAuthHeader() });
    return handleResponse(response);
  },

  get: async (id) => {
    const response = await fetch(`${API_URL}/sales-quotations/${id}`, { headers: getAuthHeader() });
    return handleResponse(response);
  },

  create: async (data) => {
    const response = await fetch(`${API_URL}/sales-quotations`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const response = await fetch(`${API_URL}/sales-quotations/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  updateStatus: async (id, status, formData) => {
    const response = await fetch(`${API_URL}/sales-quotations/${id}/status`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, ...(formData ? { formData } : {}) })
    });
    return handleResponse(response);
  }
};

export const productService = {
  list: async () => {
    const response = await fetch(`${API_URL}/products`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_URL}/products`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_URL}/products/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_URL}/products/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    return handleResponse(response);
  },
  history: async (id) => {
    const response = await fetch(`${API_URL}/products/${id}/history`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
};

export const leadService = {
  list: async () => {
    const response = await fetch(`${API_URL}/leads`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  get: async (id) => {
    const response = await fetch(`${API_URL}/leads/${id}`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_URL}/leads`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_URL}/leads/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  convert: async (id) => {
    const response = await fetch(`${API_URL}/leads/${id}/convert`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },
  unconvert: async (id) => {
    const response = await fetch(`${API_URL}/leads/${id}/unconvert`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_URL}/leads/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    return handleResponse(response);
  },
  history: async (id) => {
    const response = await fetch(`${API_URL}/leads/${id}/history`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  reorder: async (orderedIds) => {
    const response = await fetch(`${API_URL}/leads/reorder`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderedIds })
    });
    return handleResponse(response);
  },
  getVoiceNotes: async (leadId) => {
    const response = await fetch(`${API_URL}/leads/${leadId}/voice-notes`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  uploadVoiceNote: async (leadId, audioBlob, title, notes) => {
    const formData = new FormData();
    const ext = audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
    formData.append('audio', audioBlob, `recording.${ext}`);
    formData.append('title', title);
    if (notes) formData.append('notes', notes);
    const response = await fetch(`${API_URL}/leads/${leadId}/voice-notes`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    return handleResponse(response);
  },
  updateVoiceNote: async (leadId, noteId, notes) => {
    const response = await fetch(`${API_URL}/leads/${leadId}/voice-notes/${noteId}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes })
    });
    return handleResponse(response);
  },
  deleteVoiceNote: async (leadId, noteId) => {
    const response = await fetch(`${API_URL}/leads/${leadId}/voice-notes/${noteId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },
};

// Shared custom-field definitions — usable across Leads, Customers, and other entities
export const customFieldService = {
  list: async () => handleResponse(await fetch(`${API_URL}/custom-fields`, { headers: getAuthHeader() })),
  create: async (name, fieldType = 'text') => handleResponse(await fetch(`${API_URL}/custom-fields`, {
    method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, fieldType })
  })),
  delete: async (id) => handleResponse(await fetch(`${API_URL}/custom-fields/${id}`, { method: 'DELETE', headers: getAuthHeader() })),
};

export const tagService = {
  list: async () => {
    const response = await fetch(`${API_URL}/tags`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  create: async (name, color) => {
    const response = await fetch(`${API_URL}/tags`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, color })
    });
    return handleResponse(response);
  },
  update: async (id, name, color) => {
    const response = await fetch(`${API_URL}/tags/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, color })
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_URL}/tags/${id}`, { method: 'DELETE', headers: getAuthHeader() });
    return handleResponse(response);
  }
};

export const customerService = {
  list: async (droppedOut = false) => {
    const response = await fetch(`${API_URL}/customers${droppedOut ? '?droppedOut=true' : ''}`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  get: async (id) => {
    const response = await fetch(`${API_URL}/customers/${id}`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_URL}/customers`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_URL}/customers/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  delete: async (id) => {
    const response = await fetch(`${API_URL}/customers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    });
    return handleResponse(response);
  },
  dropOut: async (id, reason) => {
    const response = await fetch(`${API_URL}/customers/${id}/drop-out`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    return handleResponse(response);
  },
  reinstate: async (id) => {
    const response = await fetch(`${API_URL}/customers/${id}/reinstate`, {
      method: 'PUT',
      headers: getAuthHeader(),
    });
    return handleResponse(response);
  },
  backfill: async () => {
    const response = await fetch(`${API_URL}/customers/backfill`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
    });
    return handleResponse(response);
  },
  uploadDocument: async (customerId, file, label) => {
    const formData = new FormData();
    formData.append('file', file);
    if (label) formData.append('label', label);
    const response = await fetch(`${API_URL}/customers/${customerId}/documents`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: formData
    });
    return handleResponse(response);
  },
  deleteDocument: async (customerId, docId) => {
    const response = await fetch(`${API_URL}/customers/${customerId}/documents/${docId}`, {
      method: 'DELETE',
      headers: getAuthHeader()
    });
    return handleResponse(response);
  },
  history: async (id) => {
    const response = await fetch(`${API_URL}/customers/${id}/history`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
};

export const salesReceiptService = {
  list: async () => {
    const response = await fetch(`${API_URL}/sales-receipts`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  get: async (id) => {
    const response = await fetch(`${API_URL}/sales-receipts/${id}`, { headers: getAuthHeader() });
    return handleResponse(response);
  },
  create: async (data) => {
    const response = await fetch(`${API_URL}/sales-receipts`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  update: async (id, data) => {
    const response = await fetch(`${API_URL}/sales-receipts/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },
  updateStatus: async (id, status) => {
    const response = await fetch(`${API_URL}/sales-receipts/${id}/status`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    return handleResponse(response);
  }
};
